import React from 'react';
import { ARFlowManager } from '../components/ar/ARFlowManager';
import { Box, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTour } from '../contexts/TourContext';
import { adStudio3DSteps } from '../components/onboarding/tour-steps';
import { useMobile } from '../hooks/useMobile';
import ComingSoonWrapper from '../components/launch/ComingSoonWrapper';

const AdStudioARNewPage: React.FC = () => {
  const { startTour } = useTour();
  const isMobile = useMobile();

  return (
    <ComingSoonWrapper
      feature="3d_generation"
      title="3D Studio Coming Soon"
      description="We are working hard to get 3D Studio ready. You will be able to transform your product images into stunning 3D models with AI."
      features={[
        "AI-powered image-to-3D conversion",
        "High-quality 3D model generation",
        "Multiple export formats (GLB, USDZ)",
        "AR-ready output for mobile devices"
      ]}
      icon="zap"
    >
      <div className="min-h-screen bg-background text-foreground">
        {/* Header */}
        <div className="backdrop-blur-2xl bg-background/60 border-b border-border/30">
          <div className="px-4 sm:px-6 py-3">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)', boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)' }}>
                    <Box className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-pulse"></div>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                      3D Studio
                    </h1>
                    <p className="hidden sm:block text-muted-foreground">Transform your products into high-quality 3D models</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                {/* Tour Button */}
                {!isMobile && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      startTour(adStudio3DSteps);
                    }}
                    className="px-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-800 hover:from-blue-100 hover:to-purple-100 dark:hover:from-blue-900/30 dark:hover:to-purple-900/30 text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 transition-all duration-200 rounded text-sm flex items-center gap-2"
                  >
                    <HelpCircle className="w-4 h-4" />
                    <span className="hidden sm:inline font-medium">Tour</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-4 sm:px-6 py-6 sm:py-8">
          <ARFlowManager />
        </div>
      </div>
    </ComingSoonWrapper>
  );
};

export default AdStudioARNewPage;